import debounce from 'lodash/debounce'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { EventBus } from '../../core/smpl-components'

/**
 * ScrollTriggerManager
 * --------------------
 * This utility class is designed to manage GSAP ScrollTrigger instances efficiently.
 * It tracks all created ScrollTrigger instances, ensures they are refreshed after
 * a debounce period, and provides debugging capabilities for development.
 *
 * Key Features:
 * - Configures ScrollTrigger with custom settings
 * - Tracks all ScrollTrigger instances created via ScrollTrigger.create().
 * - Debounces refresh calls to avoid excessive refreshes when multiple ScrollTriggers
 *   are created in quick succession.
 * - Listens for resize events and automatically refreshes ScrollTriggers
 * - Provides an optional debug mode to log detailed information about ScrollTrigger
 *   creation and refresh events.
 *
 * Options:
 * - `scrollTriggerConfig` (Object): Configuration object passed to ScrollTrigger.config()
 * - `refreshDelay` (Number): The debounce time (in milliseconds) for refresh calls.
 * - `debug` (Boolean): Enable or disable debug logging (default: false).
 * - `eventBus` (Object): An EventBus instance for emitting and listening to events.
 *
 * Example Usage:
 * ---------------
 * const scrollTriggerManager = new ScrollTriggerManager({
 *     scrollTriggerConfig: {
 *         ignoreMobileResize: true,
 *     },
 *     refreshDelay: 25,
 *     debug: false,
 * });
 */
export class ScrollTriggerManager {
	constructor(options = {}) {
		// Default options
		const defaultOptions = {
			scrollTriggerConfig: {
				ignoreMobileResize: true,
			},
			refreshDelay: 100, // Time to debounce refresh calls
			debug: false, // Enable or disable debug logging
			eventBus: EventBus, // EventBus instance for emitting events
		}

		// Merge user-provided options with defaults
		this.options = { ...defaultOptions, ...options }

		this.scrollTriggerConfig = this.options.scrollTriggerConfig
		this.refreshDelay = this.options.refreshDelay
		this.debug = this.options.debug
		this.eventBus = this.options.eventBus

		this.trackedInstances = [] // Array to track all ScrollTrigger instances
		this.debouncedRefresh = debounce(this.refresh.bind(this), this.refreshDelay)

		this.init()
	}

	init() {
		// Configure ScrollTrigger with provided config
		if (this.scrollTriggerConfig) {
			ScrollTrigger.config(this.scrollTriggerConfig)

			if (this.debug) {
				console.log('[ScrollTriggerManager] Configured with:', this.scrollTriggerConfig)
			}
		}

		// Set up resize event listener
		this.setupResizeListener()

		// Wrap the original ScrollTrigger.create method
		this.wrapScrollTriggerCreate()

		if (this.debug) {
			console.log('[ScrollTriggerManager] Initialized')
		}
	}

	setupResizeListener() {
		if (this.eventBus) {
			this.eventBus.on('page:resize', this.debouncedRefresh)

			if (this.debug) {
				console.log('[ScrollTriggerManager] Listening for page:resize events')
			}
		}
	}

	wrapScrollTriggerCreate() {
		// Store original create method
		if (!ScrollTrigger.create._original) {
			ScrollTrigger.create._original = ScrollTrigger.create
		}

		const originalCreate = ScrollTrigger.create._original

		ScrollTrigger.create = (...args) => {
			// Forward all arguments to the original method
			const instance = originalCreate.apply(ScrollTrigger, args)

			// Track the instance
			this.trackInstance(instance)

			return instance
		}
	}

	trackInstance(instance) {
		// Track the created ScrollTrigger instance
		this.trackedInstances.push(instance)
		this.debouncedRefresh()

		// Log if debug is enabled
		if (this.debug) {
			console.log('[ScrollTriggerManager] ScrollTrigger created and tracked:', instance)
		}

		// Emit event if eventBus is available
		if (this.eventBus) {
			this.eventBus.emit('scrolltrigger:created', instance)
		}
	}

	refresh() {
		// Log if debug is enabled
		if (this.debug) {
			console.log('[ScrollTriggerManager] Refreshing ScrollTrigger...')
		}

		ScrollTrigger.sort() // Sort all ScrollTriggers
		ScrollTrigger.refresh() // Refresh all ScrollTriggers

		if (this.debug) {
			console.log(`[ScrollTriggerManager] Refreshed ${ScrollTrigger.getAll().length} ScrollTriggers`)
		}
	}

	sort() {
		ScrollTrigger.sort()

		if (this.debug) {
			console.log('[ScrollTriggerManager] Sorted ScrollTriggers')
		}
	}

	sortAndRefresh() {
		this.sort()
		this.refresh()
	}

	getInstances() {
		// Return all tracked ScrollTrigger instances
		return this.trackedInstances
	}

	getAllScrollTriggers() {
		// Return all ScrollTrigger instances (including non-tracked ones)
		return ScrollTrigger.getAll()
	}

	reset() {
		// Filter out ScrollTriggers whose elements no longer exist in the DOM
		this.trackedInstances = this.trackedInstances.filter((instance) => {
			const isAlive = instance.trigger && document.body.contains(instance.trigger)

			if (!isAlive && this.debug) {
				console.log('[ScrollTriggerManager] Removing dead ScrollTrigger:', instance)
			}

			return isAlive
		})

		// Log if debug is enabled
		if (this.debug) {
			console.log('[ScrollTriggerManager] Reset: Removed dead instances.')
		}

		// Refresh ScrollTrigger to ensure it's ready for new instances
		ScrollTrigger.refresh()
	}

	destroy() {
		// Remove resize event listener
		if (this.eventBus) {
			this.eventBus.off('page:resize', this.debouncedRefresh)
		}

		// Restore the original ScrollTrigger.create method
		if (ScrollTrigger.create._original) {
			ScrollTrigger.create = ScrollTrigger.create._original
		}

		// Clear tracked instances
		this.trackedInstances = []

		if (this.debug) {
			console.log('[ScrollTriggerManager] Destroyed')
		}
	}
}
